import axiosNest from '../../api/axiosNest';
import { plNumber } from '../../shared/number';

/**
 * Rejestr ofert przewoźników przy zleceniu w Trade — odpowiada
 * `/user-offers/:id/carrier-offers` w API (makieta back office v2, A9;
 * decyzja D10 z 20.10.2026; przełącznik `carrierOfferRegister`). Nic nie
 * wychodzi na zewnątrz — to wpisy tradera.
 */

/** Przełącznik funkcji — klucz z rejestru API (dane, nie przemianowujemy). */
export const FEATURE_CARRIER_OFFERS = 'carrierOfferRegister';

/** Klucz filtra listy zleceń — kontrakt z API (`trade-filters.ts`). */
export const CARRIER_OFFERS_FILTER = 'carrierOffers';

/** Wartości kolumn — dane z API, nie tłumaczymy; etykiety dla operatora obok. */
export const OFFER_KIND_CHOICES = [
  { id: 'carrier_offer', name: 'Oferta przewoźnika' },
  { id: 'counter_offer', name: 'Kontroferta tradera' },
];

export const OFFER_SOURCE_CHOICES = [
  { id: 'phone', name: 'Telefon' },
  { id: 'email', name: 'Mail' },
  { id: 'exchange', name: 'Giełda' },
  { id: 'other', name: 'Inne' },
];

/** Waluty w formularzu; API przyjmuje każdy kod ISO, kurs daje NBP. */
export const OFFER_CURRENCIES = ['EUR', 'PLN', 'CZK', 'HUF', 'CHF', 'GBP', 'SEK', 'DKK', 'NOK', 'RON'];

export const OFFER_STATUS_LABELS: Record<string, string> = {
  open: 'otwarta',
  rejected: 'odrzucona',
  accepted: 'zaakceptowana',
};

/** Akceptacja przewoźnika (paczka p. Mariusza 21.10, S-21.10-17) — klucz z rejestru API. */
export const FEATURE_CARRIER_ACCEPTANCE = 'carrierAcceptance';

/** Najkrótsze uzasadnienie przy ostrzeżeniu i powód cofnięcia — jak w API. */
export const MIN_ACCEPTANCE_REASON = 10;

/** Akceptacja przy ofercie — z API tylko przy przełączniku `carrierAcceptance`. */
export interface OfferAcceptance {
  /** Obowiązująca akceptacja przewoźnika dla zlecenia. */
  current: boolean;
  canAccept: boolean;
  acceptRefusal: string | null;
  /** Ostrzeżenia, przy których akceptacja wymaga uzasadnienia. */
  warnings: string[];
  conditions?: string | null;
  justification?: string | null;
  marginBand?: string | null;
  /** Zaakceptowana w poprzednim pobycie w Trade — nieaktualna. */
  stale?: boolean;
  /**
   * Weryfikacja przewoźnika (24.10.2026, S-24.10-11) — tylko z przełącznikiem
   * `carrierVerification`. Niepełna albo nieustalona jest też w `warnings`.
   */
  verification?: { status: 'full' | 'partial' | 'unknown'; statusLabel: string; gaps: string[] };
}

/** Pasma alertu marży — klucze jak w API (`margin.ts`). */
export const MARGIN_BAND_LABELS: Record<string, string> = {
  high: 'marża wysoka',
  medium: 'marża średnia',
  breakEven: 'marża ok. zera',
  clearlyNegative: 'marża wyraźnie ujemna',
  // Pięć pasm od kosztu frachtu (paczka p. Mariusza 21.10, S-21.10-18).
  criticalLoss: 'strata krytyczna',
  loss: 'strata',
  minimum: 'marża minimalna',
  normal: 'marża normalna',
  verify: 'do weryfikacji',
};

/** Kolor chipu pasma — straty czerwone, granica zera i minimum żółte. */
export function marginBandColor(band: string): 'error' | 'warning' | 'success' | 'info' {
  if (band === 'clearlyNegative' || band === 'criticalLoss' || band === 'loss') return 'error';
  if (band === 'breakEven' || band === 'minimum') return 'warning';
  if (band === 'verify') return 'info';
  return 'success';
}

/** Pasma, przy których akceptacja przewoźnika wymaga uzasadnienia — jak w API. */
export const LOSS_BANDS: readonly string[] = ['criticalLoss', 'loss', 'clearlyNegative'];

const label = (choices: Array<{ id: string; name: string }>, id: unknown) =>
  choices.find((c) => c.id === id)?.name ?? String(id ?? '—');

export const offerKindLabel = (v: unknown) => label(OFFER_KIND_CHOICES, v);
export const offerSourceLabel = (v: unknown) => label(OFFER_SOURCE_CHOICES, v);

export interface OfferMargin {
  marginPln: number | null;
  marginPct: number | null;
  band: string | null;
  bandRule: string | null;
  /** Pięć pasm: marża w % kosztu frachtu; brak przy czterech progach. */
  bandPct?: number | null;
  marginInOrderCurrency: { amount: number; currency: string } | null;
  offerPln: number | null;
  missing: string[];
  breakdown: string[];
  assumptions: string[];
}

export interface CarrierOffer {
  id: number;
  kind: string;
  amount: number;
  currency: string;
  validUntil: string | null;
  expired: boolean;
  source: string;
  sourceNote: string | null;
  note: string | null;
  status: string;
  statusReason: string | null;
  statusChangedBy: string | null;
  statusChangedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  carrier: { id: number; name: string; active: boolean | null };
  margin: OfferMargin;
  acceptance?: OfferAcceptance;
}

export interface CarrierOffersResponse {
  canAdd: boolean;
  addRefusal: string | null;
  orderValue: {
    amount: number | null;
    currency: string | null;
    pln: number | null;
    missing: string[];
  };
  marginAlertConfigured: boolean;
  /** Podstawa procentu pasma: wartość zlecenia (progi) albo koszt frachtu (pięć pasm). */
  marginBandBase?: 'orderValue' | 'freightCost';
  offers: CarrierOffer[];
  /** Obowiązująca akceptacja (S-21.10-17); brak pola = przełącznik wyłączony. */
  acceptedOfferId?: number | null;
  /** Przewoźnik zaakceptowany, a zlecenie dalej „Przekazane do Trade”. */
  acceptedAwaitingTms?: boolean;
}

export interface NewCarrierOffer {
  carrierId: number;
  kind: string;
  amount: string;
  currency: string;
  /** Czas polski z pola `datetime-local` (`2026-10-21T15:00`); puste = bez terminu. */
  validUntil: string;
  source: string;
  sourceNote: string;
  note: string;
}

const path = (userOfferId: number | string) => `/user-offers/${userOfferId}/carrier-offers`;

export async function fetchCarrierOffers(
  userOfferId: number | string,
): Promise<CarrierOffersResponse> {
  const { data } = await axiosNest.get<CarrierOffersResponse>(path(userOfferId));
  return data;
}

export async function createCarrierOffer(userOfferId: number | string, input: NewCarrierOffer) {
  const { data } = await axiosNest.post(path(userOfferId), {
    ...input,
    validUntil: input.validUntil.trim() || undefined,
    sourceNote: input.sourceNote.trim() || undefined,
    note: input.note.trim() || undefined,
  });
  return data;
}

export async function rejectCarrierOffer(
  userOfferId: number | string,
  offerId: number,
  reason: string,
) {
  const { data } = await axiosNest.post(`${path(userOfferId)}/${offerId}/reject`, {
    reason: reason.trim() || undefined,
  });
  return data;
}

/** Akceptacja przewoźnika i stawki — warunki i uzasadnienie (przy ostrzeżeniu wymagane). */
export async function acceptCarrierOffer(
  userOfferId: number | string,
  offerId: number,
  input: { conditions: string; justification: string },
) {
  const { data } = await axiosNest.post(`${path(userOfferId)}/${offerId}/accept`, {
    conditions: input.conditions.trim() || undefined,
    justification: input.justification.trim() || undefined,
  });
  return data;
}

/** Cofnięcie akceptacji z powodem — oferta wraca do otwartych. */
export async function revokeCarrierAcceptance(userOfferId: number | string, offerId: number, reason: string) {
  const { data } = await axiosNest.post(`${path(userOfferId)}/${offerId}/revoke-acceptance`, {
    reason: reason.trim(),
  });
  return data;
}

const symbol = (currency: string) => (currency === 'PLN' ? 'zł' : currency === 'EUR' ? '€' : currency);

/** „1150 €”, „4887,5 zł” — kwota z walutą. */
export function money(amount: number | null | undefined, currency: string | null | undefined): string {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return '—';
  return `${plNumber(amount, 2)} ${symbol((currency ?? '').toUpperCase())}`.trim();
}

/**
 * Marża słowami: „1487 zł (23,3%) · 350 €”; nieznana — „—”. Brak nigdy nie
 * jest zerem (API oddaje `null` z powodem w `missing`).
 */
export function marginText(m: OfferMargin): string {
  if (m.marginPln === null) return '—';
  const pct = m.marginPct === null ? '' : ` (${plNumber(m.marginPct, 1)}%)`;
  const own = m.marginInOrderCurrency
    ? ` · ${money(m.marginInOrderCurrency.amount, m.marginInOrderCurrency.currency)}`
    : '';
  return `${money(m.marginPln, 'PLN')}${pct}${own}`;
}

/** Dymek przy marży: rachunek albo braki. */
export function marginHint(m: OfferMargin): string {
  if (m.marginPln === null) {
    return m.missing.length > 0 ? `Nie policzono: ${m.missing.join('; ')}` : 'Nie policzono marży';
  }
  return [...m.breakdown, ...m.assumptions].join(' · ');
}
